import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import PageMeta from '@/components/base/PageMeta';
import { testimonials } from '@/mocks/comics';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('company_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('提交成功！我们会尽快回复您。');
      form.reset();
      return;
    }
    try {
      const formData = new FormData(form);
      const res = await fetch('https://readdy.ai/api/form/d9pc4paftmtnfcfh0kcg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }
      if (res.ok && (parsed as { code?: string })?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('提交成功！我们会尽快回复您。');
        form.reset();
      } else {
        setFormStatus('error');
        const msg = (parsed as { meta?: { message?: string } })?.meta?.message
          || (parsed as { message?: string })?.message
          || '提交失败，请稍后重试。';
        setFormMessage(msg);
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查网络后重试。');
    }
  };

  return (
    <>
      <PageMeta
        title="关于我们 - 韩漫网 - 韩国漫画大全"
        description="韩漫网成立于2020年，是国内领先的韩国漫画在线阅读平台，提供齐全的韩漫大全和热门漫画推荐，所有漫画免费在线观看。"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: '关于韩漫网',
          description: '韩漫网是国内领先的韩国漫画在线阅读平台，致力于为广大漫画爱好者打造最齐全的韩漫大全。',
          url: 'https://hanmanwang.com/about',
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '韩漫网首页', item: 'https://hanmanwang.com/' },
              { '@type': 'ListItem', position: 2, name: '关于我们' },
            ],
          },
          about: {
            '@type': 'Organization',
            name: '韩漫网',
            url: 'https://hanmanwang.com/',
            description: '韩漫网打造齐全的韩漫大全，免费在线观看热门韩国漫画。',
            foundingDate: '2020',
            contactPoint: {
              '@type': 'ContactPoint',
              contactType: 'customer service',
              email: 'contact@hanmanwang.com',
            },
          },
        }}
      />
      <Navbar />
      <ScrollToTop />
      <main className="min-h-screen bg-background-50 pt-16">
        {/* Hero */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-[900px] mx-auto px-4 md:px-6 text-center">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
            <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center rounded-2xl bg-primary-100 text-primary-500">
              <i className="ri-book-open-line text-3xl"></i>
            </div>
            <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 mb-4">
              关于韩漫网
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl mx-auto">
              韩漫网成立于2020年，是国内领先的韩国漫画在线阅读平台。我们致力于为广大漫画爱好者打造最齐全的韩漫大全，提供专业的热门漫画推荐，所有韩国漫画内容均可免费在线观看。平台汇聚了数千部优质韩国漫画作品，涵盖恋爱、奇幻、动作、悬疑、搞笑、惊悚、剧情、校园等多种题材，每日持续更新最新章节。
            </p>
          </div>
        </section>

        {/* Mission & Values */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-[1100px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  icon: 'ri-heart-line',
                  title: '热爱漫画',
                  desc: '我们是一群狂热的漫画爱好者，深知每一位读者对优质漫画的渴望，用心挑选每一部作品。',
                },
                {
                  icon: 'ri-shield-check-line',
                  title: '品质保证',
                  desc: '所有漫画均经过严格筛选，确保高清画质与正版授权，为用户提供最优质的阅读体验。',
                },
                {
                  icon: 'ri-flashlight-line',
                  title: '持续创新',
                  desc: '不断优化平台功能与用户体验，引入智能推荐算法，让好漫画主动找到对的读者。',
                },
              ].map((item) => (
                <div key={item.title} className="text-center p-6 rounded-xl bg-background-50">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-secondary-100 text-secondary-500">
                    <i className={`${item.icon} text-2xl`}></i>
                  </div>
                  <h3 className="text-base font-bold font-heading text-foreground-900 mb-2 whitespace-nowrap">{item.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats Timeline */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-[900px] mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 text-center mb-8">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-[19px] top-2 bottom-2 w-0.5 bg-background-200 hidden md:block"></div>
              <div className="space-y-6">
                {[
                  { year: '2020', title: '平台创立', desc: '韩漫网正式上线，初期收录500+部热门韩国漫画，迈出韩漫分享的第一步。' },
                  { year: '2021', title: '内容扩充', desc: '漫画数量突破2000部，新增分类体系与搜索功能，用户活跃度大幅提升。' },
                  { year: '2022', title: '体验升级', desc: '全面优化阅读界面与加载速度，推出智能推荐系统，月活用户突破100万。' },
                  { year: '2023', title: '社区建设', desc: '上线评论互动与收藏功能，构建漫画爱好者社区，用户粘性显著增强。' },
                  { year: '2024', title: '规模扩张', desc: '漫画总数突破8000部，日更新量超过200话，成为国内领先的韩漫平台之一。' },
                  { year: '2025', title: 'AI赋能', desc: '引入AI内容理解与个性化推荐技术，用户体验再上新台阶，注册用户突破500万。' },
                  { year: '2026', title: '持续成长', desc: '平台持续优化迭代，漫画总数突破10000部，致力于成为最全最好的韩漫阅读平台。' },
                ].map((item, index) => (
                  <div key={item.year} className="flex gap-5">
                    <div className="hidden md:flex flex-col items-center shrink-0">
                      <div className="w-10 h-10 rounded-full bg-primary-500 text-white flex items-center justify-center text-xs font-bold">
                        {item.year.slice(2)}
                      </div>
                    </div>
                    <div className="flex-1 bg-background-100 rounded-xl p-4 md:p-5">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="md:hidden text-xs font-bold text-primary-500">{item.year}</span>
                        <h3 className="text-sm font-semibold text-foreground-900 whitespace-nowrap">{item.title}</h3>
                      </div>
                      <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-[1100px] mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 text-center mb-2">
              读者口碑
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">来自真实读者的评价</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
              {testimonials.map((t) => (
                <div key={t.id} className="bg-background-50 rounded-xl p-5">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200 shrink-0">
                      <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">{t.name}</div>
                      <div className="flex items-center gap-0.5 mt-0.5">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <i
                            key={i}
                            className={`text-xs ${i < t.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                          ></i>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.comment}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-[900px] mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 text-center mb-2">
              联系我们
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              有任何问题或建议？欢迎随时与我们联系
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4">
                {[
                  { icon: 'ri-mail-line', label: '电子邮箱', value: 'contact@hanmanwang.com' },
                  { icon: 'ri-customer-service-2-line', label: '在线客服', value: '工作日 9:00 - 18:00' },
                  { icon: 'ri-wechat-line', label: '微信公众号', value: '韩漫网官方' },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-500 shrink-0">
                      <i className={`${item.icon} text-lg`}></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500 mb-0.5">{item.label}</div>
                      <div className="text-sm font-medium text-foreground-900">{item.value}</div>
                    </div>
                  </div>
                ))}
              </div>

              <form
                onSubmit={handleSubmit}
                data-readdy-form=""
                className="space-y-3"
              >
                <input
                  type="text"
                  name="company_alt"
                  className="hp-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
                <div>
                  <input
                    type="text"
                    name="name"
                    placeholder="您的姓名"
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200/70 rounded-lg text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                  />
                </div>
                <div>
                  <input
                    type="email"
                    name="email"
                    placeholder="您的邮箱"
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200/70 rounded-lg text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                  />
                </div>
                <div>
                  <textarea
                    name="message"
                    placeholder="请输入您的留言内容（500字以内）"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200/70 rounded-lg text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className="w-full px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-full text-sm font-medium transition-colors whitespace-nowrap"
                >
                  发送消息
                </button>
                {formStatus !== 'idle' && (
                  <p className={`text-center text-sm ${formStatus === 'success' ? 'text-primary-600' : 'text-red-500'}`}>
                    {formMessage}
                  </p>
                )}
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}